import { cn } from '@lib/utils';
import type { HTMLAttributes } from 'react';


type BaseProps = HTMLAttributes<HTMLDivElement>;

function Base({ className, ...rest }: BaseProps): JSX.Element {
  return (
    <div
      className={cn('animate-shimmer rounded-md bg-apple-gray-200', className)}
      {...rest}
    />
  );
}

// --- Variants ---------------------------------------------------------------

function Text({
  width = '100%',
  lines = 1,
  className,
}: {
  width?: string | number;
  lines?: number;
  className?: string;
}): JSX.Element {
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Base
          key={i}
          style={{
            width:
              lines > 1 && i === lines - 1
                ? typeof width === 'number'
                  ? Math.round(width * 0.6)
                  : '60%'
                : width,
          }}
          className="h-3.5"
        />
      ))}
    </div>
  );
}

function AvatarSkeleton({ size = 32 }: { size?: number }): JSX.Element {
  return <Base style={{ width: size, height: size }} className="rounded-full" />;
}

function CardSkeleton({ className }: { className?: string }): JSX.Element {
  return (
    <div
      className={cn(
        'rounded-2xl bg-white p-6 shadow-card flex flex-col gap-4',
        className,
      )}
    >
      <div className="flex items-center gap-3">
        <AvatarSkeleton size={40} />
        <div className="flex-1">
          <Base className="h-4 w-1/2" />
          <Base className="mt-2 h-3 w-1/3" />
        </div>
      </div>
      <Text lines={3} />
    </div>
  );
}

function StatSkeleton(): JSX.Element {
  return (
    <div className="rounded-2xl bg-white p-6 shadow-card flex flex-col gap-4">
      <Base className="h-1 w-12 rounded-full" />
      <Base className="h-3 w-24" />
      <Base className="h-9 w-32" />
      <Base className="h-3 w-20" />
    </div>
  );
}

function TableSkeleton({
  rows = 5,
  cols = 4,
}: {
  rows?: number;
  cols?: number;
}): JSX.Element {
  return (
    <div className="overflow-hidden rounded-2xl bg-white shadow-card">
      <div className="grid gap-4 bg-apple-gray-100 px-6 py-3.5" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
        {Array.from({ length: cols }).map((_, i) => (
          <Base key={i} className="h-3" />
        ))}
      </div>
      <div className="divide-y divide-apple-divider">
        {Array.from({ length: rows }).map((_, r) => (
          <div
            key={r}
            className="grid gap-4 px-6 py-4"
            style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
          >
            {Array.from({ length: cols }).map((_, c) => (
              <Base key={c} className="h-4" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export const Skeleton = Object.assign(Base, {
  Text,
  Avatar: AvatarSkeleton,
  Card: CardSkeleton,
  Stat: StatSkeleton,
  Table: TableSkeleton,
});
